import { FC, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { reaction } from 'mobx';
import { useNavigate } from 'react-router-dom';
import { useStore } from '~/stores';
import { usePlatform, ScreenOrientation } from '~/services/platform';
import {
  Container,
  Header,
  LogoImage,
  Content,
  Title,
  Subtitle,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  ErrorContainer,
  ErrorText,
  RetryButton,
  Footer,
  DeviceInfo,
  DevSkipButton,
  // Pending approval styles
  PendingContainer,
  LockIcon,
  PendingTitle,
  DeviceIdDisplay,
  DeviceModel,
  PendingMessage,
  PendingSpinner,
  PendingStatus,
  RejectedIcon,
  RejectedTitle,
  RejectedReason,
  // Orientation selector styles
  OrientationSelector,
  OrientationLabel,
  OrientationToggle,
  OrientationOption,
  AutoBadge,
} from './styles';
import logoWhite from '~/assets/images/logo-white.png';
import {
  resolveApiBaseUrl,
  resolveTenantSlug,
} from '~/services/embedded/hostConfig';

/**
 * Lock SVG icon for pending approval state.
 */
const LockSvgIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

/**
 * Close/X SVG icon for rejected state.
 */
const CloseSvgIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

/**
 * Landscape icon for orientation selector.
 */
const LandscapeIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 5H3c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-2 12H5V7h14v10z" />
  </svg>
);

/**
 * Portrait icon for orientation selector.
 */
const PortraitIcon: FC = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17 1H7c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-2-2-2zm-2 18H9V5h6v14z" />
  </svg>
);

const Auth: FC = observer(() => {
  const { authStore, syncStore } = useStore();
  const platform = usePlatform();
  const navigate = useNavigate();

  // Orientation state
  const detectedOrientation = platform.getOrientation();
  const [orientationOverride, setOrientationOverride] =
    useState<ScreenOrientation | null>(authStore.selectedOrientation);
  const selectedOrientation = orientationOverride ?? detectedOrientation;

  // Custom orientation selector state
  const [showCustomSelector, setShowCustomSelector] = useState(false);
  const customOrientations = syncStore.availableOrientations.filter(
    (o) => !o.is_default,
  );

  // Sync selected orientation with authStore
  useEffect(() => {
    authStore.setSelectedOrientation(orientationOverride);
  }, [orientationOverride, authStore]);

  // Initialize auth flow with platform service
  useEffect(() => {
    if (!authStore.isAuthenticated && authStore.registrationStatus === 'idle') {
      authStore.initializeWithPlatform(platform);
    }
  }, [authStore, platform]);

  // Redirect when authenticated - using MobX reaction for immediate reactivity
  useEffect(() => {
    console.log('[Auth] Setting up MobX reaction for authentication state');

    const dispose = reaction(
      () => ({
        isAuthenticated: authStore.isAuthenticated,
        registrationStatus: authStore.registrationStatus,
      }),
      ({ isAuthenticated, registrationStatus }) => {
        console.log('[Auth] 🔔 Reaction triggered - state changed:', {
          isAuthenticated,
          registrationStatus,
          deviceId: authStore.deviceId,
          playerId: authStore.playerId,
          shouldNavigate: isAuthenticated || registrationStatus === 'approved',
        });

        if (isAuthenticated || registrationStatus === 'approved') {
          console.log(
            '[Auth] ✅ Device is authenticated/approved - preparing navigation to /',
          );
          // Use setTimeout to ensure state is fully updated before navigation
          setTimeout(() => {
            console.log('[Auth] 🚀 Executing navigation to /');
            try {
              navigate('/', { replace: true });
              console.log('[Auth] ✅ Navigation executed successfully');
            } catch (error) {
              console.error('[Auth] ❌ Navigation failed:', error);
            }
          }, 100);
        } else {
          console.log('[Auth] ⏳ Not authenticated yet, waiting...', {
            isAuthenticated,
            registrationStatus,
          });
        }
      },
      { fireImmediately: true }, // Check immediately on mount
    );

    return () => {
      console.log('[Auth] Cleaning up MobX reaction');
      dispose();
    };
  }, [authStore, navigate]);

  const handleRetry = async (): Promise<void> => {
    await authStore.retryRegistration();
  };

  const handleSkipDev = (): void => {
    // For dev mode: simulate approval
    if (import.meta.env.DEV) {
      // Store a dev API key
      localStorage.setItem(
        'efflux_device_api_key',
        'dev_api_key_' + Date.now(),
      );
      localStorage.setItem(
        'efflux_device_id',
        authStore.deviceId || 'DEV-TEST-001',
      );
      window.location.reload();
    }
  };

  /**
   * Render initializing/registering state
   */
  const renderLoading = (): JSX.Element => (
    <LoadingContainer>
      <LoadingSpinner />
      <LoadingText>
        {authStore.registrationStatus === 'initializing'
          ? 'Identificando dispositivo...'
          : 'Registrando dispositivo...'}
      </LoadingText>
    </LoadingContainer>
  );

  /**
   * Handle orientation option click.
   * Updates local state and sends the change to the server.
   */
  const handleOrientationClick = async (
    orientation: ScreenOrientation,
  ): Promise<void> => {
    // If clicking the auto-detected option, clear the override
    const newOverride =
      orientation === detectedOrientation ? null : orientation;
    setOrientationOverride(newOverride);

    // Determine the effective orientation to send to server
    const effectiveOrientation = newOverride ?? detectedOrientation;

    // Send update to server
    await authStore.updateOrientation(effectiveOrientation);
  };

  /**
   * Handle custom orientation selection from the dropdown.
   * Sends the orientacaoId to the server which sets the player's custom orientation.
   */
  const handleCustomOrientationSelect = async (
    orientacaoId: number,
  ): Promise<void> => {
    setShowCustomSelector(false);
    await authStore.updateOrientationWithId(orientacaoId);
  };

  /**
   * Render pending approval state
   */
  const renderPendingApproval = (): JSX.Element => (
    <PendingContainer>
      <LockIcon>
        <LockSvgIcon />
      </LockIcon>

      <PendingTitle>Dispositivo Pendente</PendingTitle>

      <DeviceIdDisplay>{authStore.deviceId}</DeviceIdDisplay>

      {authStore.deviceInfo?.model && (
        <DeviceModel>
          {authStore.deviceInfo.manufacturer} {authStore.deviceInfo.model}
        </DeviceModel>
      )}

      <DeviceModel style={{ marginTop: '8px', opacity: 0.7 }}>
        Tenant: {import.meta.env.VITE_TENANT_NAME || resolveTenantSlug('N/A')}
      </DeviceModel>

      <OrientationSelector>
        <OrientationLabel>Orientação do Display:</OrientationLabel>
        <OrientationToggle>
          <OrientationOption
            $selected={selectedOrientation === 'landscape'}
            onClick={() => handleOrientationClick('landscape')}
            tabIndex={0}
            className="focusable"
          >
            <LandscapeIcon />
            Paisagem
            {detectedOrientation === 'landscape' && <AutoBadge>Auto</AutoBadge>}
          </OrientationOption>
          <OrientationOption
            $selected={selectedOrientation === 'portrait'}
            onClick={() => handleOrientationClick('portrait')}
            tabIndex={0}
            className="focusable"
          >
            <PortraitIcon />
            Retrato
            {detectedOrientation === 'portrait' && <AutoBadge>Auto</AutoBadge>}
          </OrientationOption>
          {customOrientations.length > 0 && (
            <div style={{ position: 'relative' }}>
              <OrientationOption
                $selected={false}
                onClick={() => setShowCustomSelector(!showCustomSelector)}
                tabIndex={0}
                className="focusable"
              >
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-7-2h2v-4h4v-2h-4V7h-2v4H7v2h4v4z" />
                </svg>
                Custom
              </OrientationOption>
              {showCustomSelector && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '8px',
                    background: 'rgba(30, 30, 50, 0.95)',
                    border: '2px solid rgba(255, 255, 255, 0.15)',
                    borderRadius: '12px',
                    padding: '8px 0',
                    minWidth: '260px',
                    zIndex: 100,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  {customOrientations.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => handleCustomOrientationSelect(o.id)}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '12px 20px',
                        background: 'transparent',
                        border: 'none',
                        color: '#e0e0e0',
                        fontSize: '18px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        transition: 'background 150ms',
                      }}
                      onMouseEnter={(e) => {
                        (e.target as HTMLButtonElement).style.background =
                          'rgba(33, 150, 243, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        (e.target as HTMLButtonElement).style.background =
                          'transparent';
                      }}
                      tabIndex={0}
                      className="focusable"
                    >
                      {o.name}
                      {o.width && o.height ? (
                        <span
                          style={{
                            color: '#888',
                            fontSize: '14px',
                            marginLeft: '8px',
                          }}
                        >
                          ({o.width}&times;{o.height})
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </OrientationToggle>
      </OrientationSelector>

      <PendingMessage>
        Solicite a liberação deste dispositivo no painel administrativo.
      </PendingMessage>

      <PendingStatus>
        <PendingSpinner />
        Aguardando aprovação...
      </PendingStatus>
    </PendingContainer>
  );

  /**
   * Device 'inactive'/'blocked' → o server responde `rejected` com o motivo
   * "Device is blocked" (vide DeviceRegistrationService.handleExistingDevice).
   * Nesses casos mostramos a MESMA mensagem "Player bloqueado" do fluxo de sync
   * (syncStore.isPlayerBlocked) — inactive e blocked são a mesma coisa pro
   * player. Rejeição GENUÍNA do admin (outro motivo) mantém "Acesso Negado".
   */
  const isBlockedRejection =
    authStore.registrationStatus === 'rejected' &&
    (authStore.rejectionReason ?? '').toLowerCase().includes('block');

  /**
   * Render rejected state
   */
  const renderRejected = (): JSX.Element => (
    <PendingContainer>
      <RejectedIcon>
        <CloseSvgIcon />
      </RejectedIcon>

      <RejectedTitle>
        {isBlockedRejection ? 'Player bloqueado' : 'Dispositivo Rejeitado'}
      </RejectedTitle>

      <DeviceIdDisplay>{authStore.deviceId}</DeviceIdDisplay>

      {/* Motivo técnico do server só quando NÃO for bloqueio (evita expor
          "Device is blocked" cru pro operador — o texto abaixo já explica). */}
      {!isBlockedRejection && authStore.rejectionReason && (
        <RejectedReason>{authStore.rejectionReason}</RejectedReason>
      )}

      <PendingMessage>
        {isBlockedRejection
          ? 'Entre em contato com o administrador para reativar o dispositivo.'
          : 'Entre em contato com o administrador para liberar este dispositivo.'}
      </PendingMessage>

      <RetryButton tabIndex={0} className="focusable" onClick={handleRetry}>
        Tentar novamente
      </RetryButton>
    </PendingContainer>
  );

  /**
   * Render error state
   */
  const renderError = (): JSX.Element => (
    <ErrorContainer>
      <ErrorText>{authStore.error}</ErrorText>
      <RetryButton tabIndex={0} className="focusable" onClick={handleRetry}>
        Tentar novamente
      </RetryButton>
    </ErrorContainer>
  );

  /**
   * Render content based on registration status
   */
  const renderContent = (): JSX.Element => {
    switch (authStore.registrationStatus) {
      case 'idle':
      case 'initializing':
      case 'registering':
        return renderLoading();

      case 'pending_approval':
        return renderPendingApproval();

      case 'rejected':
        return renderRejected();

      case 'error':
        return renderError();

      case 'approved':
        // Will redirect via useEffect
        return renderLoading();

      default:
        return renderLoading();
    }
  };

  return (
    <Container>
      <Header>
        <LogoImage src={logoWhite} alt="Efflux" />
      </Header>

      <Content>
        {authStore.registrationStatus === 'pending_approval' ? (
          // No title/subtitle for pending - it has its own title
          renderContent()
        ) : (
          <>
            <Title>
              {authStore.registrationStatus === 'rejected'
                ? isBlockedRejection
                  ? 'Player bloqueado'
                  : 'Acesso Negado'
                : 'Ativação do Dispositivo'}
            </Title>
            <Subtitle>
              {authStore.registrationStatus === 'rejected'
                ? isBlockedRejection
                  ? 'Este player foi bloqueado por um administrador e não está exibindo conteúdo.'
                  : 'Este dispositivo foi rejeitado pelo administrador'
                : 'Aguarde enquanto identificamos e registramos este dispositivo'}
            </Subtitle>
            {renderContent()}
          </>
        )}
      </Content>

      <Footer>
        <DeviceInfo>
          {/*
            2026-05-24 — Versão obrigatória no footer pra troubleshooting
            remoto. Mesmo padrão da NoContent. VITE_APP_VERSION é injetado
            no build-time pelo vite.config.ts.
          */}
          {import.meta.env.VITE_APP_VERSION && (
            <p>Versão: v{import.meta.env.VITE_APP_VERSION}</p>
          )}
          <p>Device ID: {authStore.deviceId || 'Obtendo...'}</p>
          {/*
            Renato 2026-06-13: usar tenantSlug (não tenantName) como fonte
            de verdade — é o slug que vai pro registerDevice e identifica o
            tenant no backend. tenantName pode estar undefined em builds web
            sem VITE_TENANT_NAME, confundindo troubleshooting.
          */}
          <p>Tenant: {authStore.tenantSlug}</p>
          <p>Server: {resolveApiBaseUrl()}</p>
        </DeviceInfo>
        {/*
          Pentest 2026-05-22 C9 / re-confirmado R3: botão "Pular autenticação"
          era exibido em hostnames `*.web.app` e `*.firebaseapp.com` — que
          incluem o player de produção (`efflux-player-demo.web.app`).
          Qualquer visitante bypassava o flow de aprovação manual e injetava
          `efflux_device_id` arbitrário no localStorage.

          Fix: gate só em `import.meta.env.DEV` (true apenas em vite dev
          server local). Em build de produção, esse bloco é removido via
          tree-shaking — o `DevSkipButton` nem chega no bundle.
        */}
        {import.meta.env.DEV && (
          <DevSkipButton
            tabIndex={0}
            className="focusable"
            onClick={handleSkipDev}
          >
            [DEV] Pular autenticação
          </DevSkipButton>
        )}
      </Footer>
    </Container>
  );
});

export default Auth;
